/**
 * The XOP Networks price list, as this portal sees it.
 *
 * PORTED, NARROWED. The enums, the labels and the three helpers below are a
 * verbatim copy of xop-edge-staff's `packages/shared/src/priceList.ts` — the
 * two apps must label a category, print a price and search an item
 * identically, and the ported quote builder in `apps/web/src/crm` imports
 * these symbols by name (it says `@xop/dashboard-shared`; the port rewrites
 * that to this package, as `index.ts` explains).
 *
 * The ITEM is narrower on purpose. crm-api's reseller index (spec §4.2)
 * serves ACTIVE items only and omits XOP Networks' internal `notes`, so the
 * ten keys below are the whole contract a reseller ever sees — spelling out
 * `active: boolean` here would declare a field that is always true and
 * `notes` one that is always absent. `PriceListPickerItem` is the widened
 * shape the ported MUI picker wants; the portal's own adapter
 * (`apps/web/src/pages/portal/priceList.ts`) is the single place that widens
 * one into the other.
 *
 * WHY A COPY AND NOT A DEPENDENCY: this repo has no access to the
 * xop-edge-staff workspace, and a handful of small pure functions is cheaper
 * to carry than a package boundary between two repos. Same call, same
 * reasoning, as `shareChat.ts` / `verticals.ts` / `pdfBytes.ts`.
 */

/** Where an item sits in the price book. Mirrors the printed book's sections. */
export const PRICE_LIST_CATEGORIES = [
  "value_added_service",
  "platform_extension",
  "demo_system",
  "support",
  "professional_services",
  "training",
  "hardware",
  "other",
] as const;

export type PriceListCategory = (typeof PRICE_LIST_CATEGORIES)[number];

export const PRICE_LIST_CATEGORY_LABELS: Record<PriceListCategory, string> = {
  value_added_service: "Value Added Services",
  platform_extension: "Platform Extensions",
  demo_system: "Demo System",
  support: "Support & Warranty",
  professional_services: "Professional Services",
  training: "Training",
  hardware: "Hardware",
  other: "Other",
};

/**
 * The TWO sections a licence's features are grouped into — everywhere in the
 * XOP system, in this order, exactly as the C1 appliance's own licence page
 * shows them (CEO, 2026-09-15): "Value Added Services" first, "Platform
 * Extensions" second.
 *
 * The rule is one line, and crm-api owns it (`PriceBookEntry.licence_section_for`):
 * a feature whose price-book category is `value_added_service` is a Value
 * Added Service; EVERY other licensable feature — whatever its category, and
 * any appliance key nobody sells — is a Platform Extension. crm-api's
 * `GET /api/v1/licences/catalog` stamps `section` on every row; a client
 * falls back to `licenceSectionOf` only for a row that arrived without one,
 * so the two can never disagree about a row the API has already placed.
 * Mirrored verbatim in xop-edge-staff's `priceList.ts`.
 */
export const LICENCE_SECTIONS = ["value_added_services", "platform_extensions"] as const;

export type LicenceSection = (typeof LICENCE_SECTIONS)[number];

export const LICENCE_SECTION_LABELS: Record<LicenceSection, string> = {
  value_added_services: "Value Added Services",
  platform_extensions: "Platform Extensions",
};

/** Which of the two licence sections a price-book category falls in. An
 * absent category (an appliance key with no catalogue row) is a Platform
 * Extension — nothing is ever left out of both. */
export function licenceSectionOf(
  category: PriceListCategory | null | undefined,
): LicenceSection {
  return category === "value_added_service" ? "value_added_services" : "platform_extensions";
}

export function isLicenceSection(v: unknown): v is LicenceSection {
  return typeof v === "string" && (LICENCE_SECTIONS as readonly string[]).includes(v);
}

/** What one unit of the item IS — printed after the price ("$166 / port"). */
export const PRICE_LIST_UNITS = [
  "each",
  "port",
  "session",
  "seat",
  "user",
  "switch",
  "system",
  "month",
  "year",
  "hour",
] as const;

export type PriceListUnit = (typeof PRICE_LIST_UNITS)[number];

export const PRICE_LIST_UNIT_LABELS: Record<PriceListUnit, string> = {
  each: "each",
  port: "per port",
  session: "per session",
  seat: "per seat",
  user: "per user",
  switch: "per switch",
  system: "per system",
  month: "per month",
  year: "per year",
  hour: "per hour",
};

/**
 * How the item is priced. Three modes because the real price book has three:
 * most rows carry a currency MSRP, the WebRTC package is bundled at no
 * charge, and the Extended Support Package is quoted as a percentage of the
 * licences purchased. Flattening the last two into "0" would make the list lie.
 *
 * All three live here because this enum is the COMMAND CENTER's enum, ported
 * whole. What a reseller may pick is a narrower question, and it is answered
 * one layer out: the BFF's projection drops `percent` rows entirely (see
 * `apps/server/src/routes/priceList.ts`), so `percent` reaches this portal's
 * type system but never its wire.
 */
export const PRICE_LIST_PRICING_MODES = ["fixed", "included", "percent"] as const;

export type PriceListPricingMode = (typeof PRICE_LIST_PRICING_MODES)[number];

/** One catalogue row, as the BFF projects it for a reseller (spec §6). */
export interface PriceListItem {
  id: number;
  /** XOP Networks part number, e.g. `700-2341-001`. Empty string when the
   * book row has none — the reseller projection never sends null, so no
   * caller has to guess which absence it is looking at. */
  part_number: string;
  description: string;
  category: PriceListCategory;
  unit: PriceListUnit;
  pricing_mode: PriceListPricingMode;
  /** Minor units (cents) — integers only, so no float drift accumulates over
   * a 30-line quote. Always 0 unless `pricing_mode === "fixed"`. */
  price_cents: number;
  /** Percentage points (15 = "15% of the order"), null off `percent` mode.
   * Always null on the wire in THIS portal — see the pricing-mode note. */
  percent_rate: number | null;
  currency: string;
  /** Manual ordering within a category; ties break on part number. */
  sort_order: number;
}

/**
 * The shape the ported picker reads. The two extra keys exist ONLY because
 * the ported `products-table.tsx` filters on `active` and searches `notes`;
 * a reseller's list is active-only and note-free, so the adapter fills them
 * with the constants that make the ported code behave.
 */
export interface PriceListPickerItem extends PriceListItem {
  notes: string;
  active: boolean;
}

/** `GET /api/crm/price-list`. */
/**
 * One thing a reseller may ASK for, with no money in it.
 *
 * This is the whole contract behind the request form: an identity, what it
 * is, and the UNIT the reseller types a quantity of. There is deliberately no
 * price field anywhere in this shape — see the `/quote-catalog` route.
 */
export interface QuoteCatalogItem {
  id: number;
  part_number: string;
  description: string;
  unit: PriceListUnit;
  category: PriceListCategory;
}

export interface QuoteCatalogResponse {
  items: QuoteCatalogItem[];
}

export interface PriceListResponse {
  items: PriceListItem[];
}

export function isPriceListCategory(v: unknown): v is PriceListCategory {
  return typeof v === "string" && (PRICE_LIST_CATEGORIES as readonly string[]).includes(v);
}

export function isPriceListUnit(v: unknown): v is PriceListUnit {
  return typeof v === "string" && (PRICE_LIST_UNITS as readonly string[]).includes(v);
}

export function isPriceListPricingMode(v: unknown): v is PriceListPricingMode {
  return typeof v === "string" && (PRICE_LIST_PRICING_MODES as readonly string[]).includes(v);
}

/**
 * The unit price a document line should start from, in whole currency units.
 * `included` is genuinely free and `percent` cannot be resolved without an
 * order total, so both seed the line at 0.
 *
 * A `percent` item never reaches this portal at all — the BFF drops those
 * rows, because the locked-price ruling (spec §6) leaves the reseller no way
 * to finish such a line. A percentage arrangement is a conversation with XOP
 * Networks, not a line a reseller quotes alone. The branch survives here
 * anyway, verbatim from the command center, so the ported code that calls
 * this function behaves identically in both apps.
 */
export function unitPriceFor(item: Pick<PriceListItem, "pricing_mode" | "price_cents">): number {
  return item.pricing_mode === "fixed" ? item.price_cents / 100 : 0;
}

/**
 * One-line price summary for a picker option: "$166.00 per port", "Included",
 * "15% of order". Formatting only — every call site sits behind
 * `invoices:view`, which crm-api enforces.
 */
export function formatPriceListPrice(
  item: Pick<PriceListItem, "pricing_mode" | "percent_rate" | "price_cents" | "currency" | "unit">,
): string {
  if (item.pricing_mode === "included") return "Included";
  if (item.pricing_mode === "percent") return `${item.percent_rate ?? 0}% of order`;

  const amount = (item.price_cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: item.currency || "USD",
  });
  return item.unit === "each" ? amount : `${amount} ${PRICE_LIST_UNIT_LABELS[item.unit]}`;
}

/**
 * Search haystack for an item — part number, description and, where the
 * caller has them, notes. `notes` is optional so the same function serves the
 * BFF projection (which has none) and the picker shape (which does).
 */
export function priceListSearchText(item: {
  part_number: string;
  description: string;
  notes?: string;
}): string {
  return `${item.part_number} ${item.description} ${item.notes ?? ""}`.toLowerCase();
}
